import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Toolbar,
    Chip,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
    Divider,
    Button,
    Pagination,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import axios from 'axios';
import { API } from '../../../utils/Api';

// React Toastify
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { CurrencyRupeeSharp, KeyboardArrowLeft, LocalMallOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const PettyCashTransactions = () => {
    const navigate = useNavigate();
    const token = localStorage.getItem('token');
    const storedOutletData = localStorage.getItem('outletData');
    const outletData = JSON.parse(storedOutletData || '{}');

    const [transactions, setTransactions] = useState([]);
    const [loadingTxns, setLoadingTxns] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Filter states
    const [typeFilter, setTypeFilter] = useState('ALL'); // ALL | CREDIT | DEBIT
    const [startDate, setStartDate] = useState('');      // yyyy-mm-dd
    const [endDate, setEndDate] = useState('');          // yyyy-mm-dd

    // Delete state
    const [deletingId, setDeletingId] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selectedTxn, setSelectedTxn] = useState(null);

    const formatAmount = value =>
        Number(value || 0).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

    const formatDateTime = iso => {
        if (!iso) return '-';
        const d = new Date(iso);
        return d.toLocaleString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
    };

    const getPettyCashTransactions = async (filterType, sDate, eDate) => {
        try {
            setLoadingTxns(true);
            const requestUrl = `${API.BASE_URL}outlet/petty-cash/transactions`;

            const params = {
                outlet: outletData?._id,
            };

            if (filterType && filterType !== 'ALL') {
                params.type = filterType;
            }

            if (sDate) {
                params.fromDate = sDate;
            }

            if (eDate) {
                params.toDate = eDate;
            }

            const response = await axios.get(requestUrl, {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });

            const list =
                response?.data?.data?.outletPettyCashTransactions || [];
            setTransactions(list);
        } catch (error) {
            console.error('Error fetching petty cash transactions:', error);
            // toast.error('Failed to load transactions. Please try again.');
        } finally {
            setLoadingTxns(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (!selectedTxn?._id) return;

        const txnId = selectedTxn._id;

        try {
            setDeletingId(txnId);
            const deleteUrl = `${API.BASE_URL}outlet/petty-cash/transactions`;

            await axios.delete(deleteUrl, {
                data: { _id: txnId },
                headers: { Authorization: `Bearer ${token}` },
            });

            toast.success('Transaction deleted successfully.');

            await getPettyCashTransactions(typeFilter, startDate, endDate);
        } catch (error) {
            console.error('Error deleting petty cash transaction:', error);
            toast.error('Failed to delete transaction. Please try again.');
        } finally {
            setDeletingId(null);
            setConfirmOpen(false);
            setSelectedTxn(null);
        }
    };

    const openDeleteDialog = txn => {
        setSelectedTxn(txn);
        setConfirmOpen(true);
    };

    const handleDialogClose = () => {
        if (!deletingId) {
            setConfirmOpen(false);
            setSelectedTxn(null);
        }
    };

    // Fetch whenever outlet or filters change
    useEffect(() => {
        if (!outletData?._id) return;
        setCurrentPage(1);
        getPettyCashTransactions(typeFilter, startDate, endDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outletData?._id, typeFilter, startDate, endDate]);

    // Pagination based on API-filtered result
    const totalPages =
        Math.ceil(transactions.length / itemsPerPage) || 1;

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const paginatedTransactions = transactions.slice(
        indexOfFirstItem,
        indexOfLastItem
    );

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const renderItemsTable = items => {
        if (!items || items.length === 0) {
            return (
                <Typography variant="body2" color="text.secondary">
                    No item breakdown available.
                </Typography>
            );
        }

        return (
            <Box sx={{ mt: 0.5 }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr 1fr',
                        fontSize: 12,
                        fontWeight: 600,
                        color: 'text.secondary',
                        mb: 0.5,
                    }}
                >
                    <Box>Item</Box>
                    <Box textAlign="right">Qty</Box>
                    <Box textAlign="right">Amount</Box>
                </Box>
                <Divider sx={{ mb: 0.5 }} />
                {items.map(it => {
                    const amt = (it.quantity || 0) * (it.price || 0);
                    return (
                        <Box
                            key={it._id}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '2fr 1fr 1fr',
                                fontSize: 12,
                                mb: 0.25,
                            }}
                        >
                            <Box>{it.itemName}</Box>
                            <Box textAlign="right">{it.quantity}</Box>
                            <Box textAlign="right">₹{it.amount}</Box>
                        </Box>
                    );
                })}
            </Box>
        );
    };

    return (
        <>
            <Box sx={{ backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
                <Toolbar />

                <Box
                    sx={{
                        maxWidth: 900,
                        mx: 'auto',
                        mt: 8,
                        px: 3,
                        pb: 4,
                    }}
                >
                    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <KeyboardArrowLeft onClick={() => navigate(-1)} fontSize='large' sx={{ cursor: 'pointer' }} />
                            <Box
                                sx={(theme) => ({
                                    width: 48,
                                    height: 48,
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: theme.palette.primary.light + '22',
                                    color: theme.palette.primary.main,
                                })}
                            >
                                <CurrencyRupeeSharp fontSize="medium" />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                                <Typography variant="h5" fontWeight={700} color="#003049">
                                    Petty Cash History
                                </Typography>
                            </Box>
                        </Box>

                        {transactions.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                                Last updated: {formatDateTime(transactions[0]?.createdAt)}
                            </Typography>
                        )}
                    </Box>

                    <Paper
                        elevation={3}
                        sx={{
                            borderRadius: 3,
                            p: { xs: 2.5, md: 3 },
                            overflow: 'hidden',
                        }}
                    >
                        {/* Header row */}
                        <Box
                            sx={{
                                mb: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight={600}>
                                Transactions
                            </Typography>
                            <Chip
                                size="small"
                                label={`${transactions.length} entr${transactions.length === 1 ? 'y' : 'ies'
                                    }`}
                                sx={{ fontSize: 11, height: 22 }}
                            />
                        </Box>

                        {/* Filters */}
                        <Box
                            sx={{
                                mb: 2,
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1.5,
                                alignItems: 'center',
                            }}
                        >
                            <ToggleButtonGroup
                                size="small"
                                value={typeFilter}
                                exclusive
                                onChange={(e, val) => {
                                    if (val) setTypeFilter(val);
                                }}
                            >
                                <ToggleButton value="ALL">All</ToggleButton>
                                <ToggleButton value="CREDIT">Credit</ToggleButton>
                                <ToggleButton value="DEBIT">Debit</ToggleButton>
                            </ToggleButtonGroup>
                            <TextField
                                label="Start Date"
                                type="date"
                                size="small"
                                value={startDate}
                                onChange={e => setStartDate(e.target.value)}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                            <TextField
                                label="End Date"
                                type="date"
                                size="small"
                                value={endDate}
                                onChange={e => setEndDate(e.target.value)}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />

                            {(startDate || endDate || typeFilter !== 'ALL') && (
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => {
                                        setTypeFilter('ALL');
                                        setStartDate('');
                                        setEndDate('');
                                    }}
                                >
                                    Clear filters
                                </Button>
                            )}
                        </Box>

                        {/* Content */}
                        {loadingTxns ? (
                            <Box
                                sx={{
                                    py: 4,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                }}
                            >
                                <CircularProgress size={22} />
                                <Typography variant="body2">
                                    Fetching transactions...
                                </Typography>
                            </Box>
                        ) : paginatedTransactions.length === 0 ? (
                            <Box sx={{ py: 4, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    No petty cash transactions to display for this outlet.
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                {paginatedTransactions.map((txn, index) => {
                                    const globalIndex = indexOfFirstItem + index + 1;
                                    const typeUpper = txn.type?.toUpperCase() || '-';
                                    const isCredit = typeUpper === 'CREDIT';
                                    const isDebit = typeUpper === 'DEBIT';
                                    const amountColor = isCredit ? '#2e7d32' : '#c62828';

                                    const HeaderContent = (
                                        <Stack
                                            direction="row"
                                            spacing={1.5}
                                            alignItems="center"
                                            sx={{ width: '100%' }}
                                        >
                                            <Box
                                                sx={{
                                                    minWidth: 32,
                                                    height: 32,
                                                    borderRadius: '999px',
                                                    backgroundColor: '#edf2f7',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    color: '#003049',
                                                }}
                                            >
                                                {globalIndex}
                                            </Box>

                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 600,
                                                        textTransform: 'capitalize',
                                                    }}
                                                >
                                                    {txn.remark || (isCredit ? 'Credit entry' : 'Debit entry')}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {formatDateTime(txn.createdAt)}
                                                </Typography>

                                                {isDebit &&
                                                    txn.items &&
                                                    txn.items.length > 0 && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                display: 'block',
                                                                mt: 0.3,
                                                            }}
                                                            color="text.secondary"
                                                        >
                                                            {txn.items.length} item
                                                            {txn.items.length > 1
                                                                ? 's'
                                                                : ''}
                                                        </Typography>
                                                    )}

                                                {txn.invoice && (
                                                    <Button
                                                        size="small"
                                                        variant="text"
                                                        sx={{
                                                            mt: 0.3,
                                                            px: 0,
                                                            textTransform: 'none',
                                                            fontSize: 11,
                                                        }}
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            window.open(
                                                                txn.invoice,
                                                                '_blank'
                                                            );
                                                        }}
                                                    >
                                                        View invoice
                                                    </Button>
                                                )}
                                            </Box>
                                            <Stack
                                                direction="column"
                                                spacing={0.5}
                                                sx={{
                                                    minWidth: 150,
                                                    alignItems: 'flex-end',
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: amountColor,
                                                    }}
                                                >
                                                    ₹ {formatAmount(txn.amount)}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {isDebit && (
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            color="error"
                                                            startIcon={<DeleteOutlineIcon />}
                                                            sx={{
                                                                mt: 0.5,
                                                                textTransform: 'none',
                                                                fontSize: 11,
                                                                borderRadius: '999px',
                                                                px: 1.5,
                                                            }}
                                                            disabled={deletingId === txn._id}
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                openDeleteDialog(txn);
                                                            }}
                                                        >
                                                            {deletingId === txn._id
                                                                ? 'Deleting...'
                                                                : 'Delete'}
                                                        </Button>
                                                    )}
                                                    <Chip
                                                        label={typeUpper}
                                                        size="small"
                                                        sx={{
                                                            fontSize: 11,
                                                            height: 22,
                                                            backgroundColor: isCredit
                                                                ? 'rgba(46, 125, 50, 0.1)'
                                                                : 'rgba(198, 40, 40, 0.1)',
                                                            color: isCredit
                                                                ? '#2e7d32'
                                                                : '#c62828',
                                                            fontWeight: 600,
                                                        }}
                                                    />
                                                </Box>
                                            </Stack>
                                        </Stack>
                                    );

                                    if (isDebit) {
                                        return (
                                            <Accordion
                                                key={txn._id}
                                                disableGutters
                                                sx={{
                                                    mb: 1,
                                                    borderRadius: 2,
                                                    border: '1px solid #e0e0e0',
                                                    '&:before': { display: 'none' },
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <AccordionSummary
                                                    expandIcon={<ExpandMoreIcon />}
                                                    sx={{
                                                        px: 1.5,
                                                        py: 1,
                                                        '& .MuiAccordionSummary-content':
                                                        {
                                                            margin: 0,
                                                        },
                                                    }}
                                                >
                                                    {HeaderContent}
                                                </AccordionSummary>

                                                <AccordionDetails
                                                    sx={{ px: 2, pb: 1.5 }}
                                                >
                                                    {renderItemsTable(txn.items)}
                                                </AccordionDetails>
                                            </Accordion>
                                        );
                                    }

                                    return (
                                        <Paper
                                            key={txn._id}
                                            elevation={0}
                                            sx={{
                                                mb: 1,
                                                borderRadius: 2,
                                                border: '1px solid #e0e0e0',
                                                px: 1.5,
                                                py: 1,
                                                backgroundColor: '#fafbfc',
                                            }}
                                        >
                                            {HeaderContent}
                                        </Paper>
                                    );
                                })}

                                {transactions.length > itemsPerPage && (
                                    <Box
                                        sx={{
                                            mt: 2,
                                            display: 'flex',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Pagination
                                            count={totalPages}
                                            page={currentPage}
                                            onChange={handlePageChange}
                                            color="primary"
                                            variant="outlined"
                                            shape="rounded"
                                        />
                                    </Box>
                                )}
                            </>
                        )}
                    </Paper>
                </Box>
            </Box>

            <Dialog
                open={confirmOpen}
                onClose={handleDialogClose}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Delete transaction?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this debit transaction?
                        This action cannot be undone.
                    </DialogContentText>
                    {selectedTxn && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" fontWeight={600}>
                                {selectedTxn.remark}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Amount: ₹ {formatAmount(selectedTxn.amount)}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} disabled={!!deletingId}>
                        Cancel
                    </Button>
                    <Button
                        color="error"
                        onClick={handleConfirmDelete}
                        disabled={!!deletingId}
                    >
                        {deletingId ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Toastify container – remove this if you already have one at app root */}
            <ToastContainer
                position="bottom-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
            />
        </>
    );
};

export default PettyCashTransactions;
